// Single activity log entry row. Shows a 26px coloured icon circle, the
// resolved actor name, the pre-rendered display summary, and a relative
// timestamp. Reused by ActivityLogSection (inline) and ActivityLogDrawer (full
// history). Icon colour conveys the event category. Milestone lifecycle
// event types (milestone_delivered, milestone_partially_accepted,
// milestone_accepted, milestone_paid, milestone_state_reverted) fall
// through to displaySummary verbatim with appropriately-toned icons.

import { clsx } from "clsx";
import {
  Plus,
  Pencil,
  Archive,
  RefreshCw,
  Link,
  User,
  Download,
  CheckCircle,
  Undo2,
} from "lucide-react";
import { useUserDisplay } from "../../../hooks/useUsers";
import { formatDayMonthYearLocal } from "../../../utils/formatShortDate";
import type { ActivityEntry, ActivityEventType } from "../../../types";

interface ActivityLogItemProps {
  entry: ActivityEntry;
}

interface IconConfig {
  icon: React.ReactNode;
  className: string;
}

const ICON_MAP: Record<ActivityEventType, IconConfig> = {
  entity_created: {
    icon: <Plus className="h-3.5 w-3.5" aria-hidden="true" />,
    className:
      "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400",
  },
  entity_updated: {
    icon: <Pencil className="h-3.5 w-3.5" aria-hidden="true" />,
    className:
      "bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400",
  },
  field_updated: {
    icon: <Pencil className="h-3.5 w-3.5" aria-hidden="true" />,
    className:
      "bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400",
  },
  note_updated: {
    icon: <Pencil className="h-3.5 w-3.5" aria-hidden="true" />,
    className:
      "bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400",
  },
  entity_archived: {
    icon: <Archive className="h-3.5 w-3.5" aria-hidden="true" />,
    className:
      "bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400",
  },
  entity_status_changed: {
    icon: <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />,
    className:
      "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
  },
  contract_status_changed: {
    icon: <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />,
    className:
      "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
  },
  evidence_link_added: {
    icon: <Link className="h-3.5 w-3.5" aria-hidden="true" />,
    className: "bg-sky-100 text-sky-600 dark:bg-sky-900/30 dark:text-sky-400",
  },
  evidence_link_removed: {
    icon: <Link className="h-3.5 w-3.5" aria-hidden="true" />,
    className: "bg-sky-100 text-sky-600 dark:bg-sky-900/30 dark:text-sky-400",
  },
  person_assigned: {
    icon: <User className="h-3.5 w-3.5" aria-hidden="true" />,
    className:
      "bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400",
  },
  person_unassigned: {
    icon: <User className="h-3.5 w-3.5" aria-hidden="true" />,
    className:
      "bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400",
  },
  export_generated: {
    icon: <Download className="h-3.5 w-3.5" aria-hidden="true" />,
    className:
      "bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400",
  },
  milestone_delivered: {
    icon: <CheckCircle className="h-3.5 w-3.5" aria-hidden="true" />,
    className: "bg-sky-100 text-sky-600 dark:bg-sky-900/30 dark:text-sky-400",
  },
  milestone_partially_accepted: {
    icon: <CheckCircle className="h-3.5 w-3.5" aria-hidden="true" />,
    className:
      "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
  },
  milestone_accepted: {
    icon: <CheckCircle className="h-3.5 w-3.5" aria-hidden="true" />,
    className:
      "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400",
  },
  milestone_paid: {
    icon: <CheckCircle className="h-3.5 w-3.5" aria-hidden="true" />,
    className:
      "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400",
  },
  milestone_state_reverted: {
    icon: <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />,
    className:
      "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
  },
};

function formatRelativeTime(occurredAt: string): string {
  const now = Date.now();
  const then = new Date(occurredAt).getTime();
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMins < 60) {
    return diffMins <= 1 ? "1 min ago" : `${diffMins} mins ago`;
  }
  if (diffHours < 24) {
    return diffHours === 1 ? "1 hour ago" : `${diffHours} hours ago`;
  }
  if (diffDays < 7) {
    return diffDays === 1 ? "1 day ago" : `${diffDays} days ago`;
  }
  // Absolute date for anything older than 7 days, keyed to the local
  // calendar day so it matches what the user saw happen.
  return formatDayMonthYearLocal(occurredAt);
}

export default function ActivityLogItem({
  entry,
}: ActivityLogItemProps) {
  // System-derived entries (e.g. auto-RAG flips on lifecycle transitions)
  // carry actorUserId = null deliberately so the activity log doesn't
  // mis-attribute automation to whoever happened to trigger the
  // surrounding user-driven write. Render "System" for those; otherwise
  // resolve the actor name (falling back to "Unknown user" only when
  // the lookup itself fails for a non-null id).
  const { data: actor } = useUserDisplay(entry.actorUserId ?? undefined);
  const actorName =
    entry.actorUserId == null ? "System" : (actor?.name ?? "Unknown user");
  const iconConfig = ICON_MAP[entry.eventType] ?? ICON_MAP.entity_updated;

  return (
    <div className="flex items-start gap-3 py-2" role="listitem">
      <div
        data-activity-icon
        className={clsx(
          "flex h-6 w-6 shrink-0 items-center justify-center rounded-full",
          iconConfig.className,
        )}
      >
        {iconConfig.icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-body-sm text-slate-800 dark:text-slate-200">
          <span className="font-medium">{actorName}</span>{" "}
          <span className="italic">{entry.displaySummary}</span>
        </div>
        <div className="mt-0.5 text-caption text-slate-500 dark:text-slate-400">
          {formatRelativeTime(entry.occurredAt)}
        </div>
      </div>
    </div>
  );
}
